/**
 * Bill Score Quiz — viral lead magnet
 * ====================================
 * 5-question interactive quiz → 0-100 score with letter grade.
 * Sharable badge ("I scored 87 — what's yours?") using the existing
 * /api/badge.svg endpoint. Optional email capture for tailored plan.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft, ArrowRight, Sparkles, Mail, Loader2, CheckCircle2,
  Trophy, RefreshCcw, Twitter, Linkedin, Copy, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import FAQSection from '../components/FAQSection';
import { faqsByPage } from '../data/faqs';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROD = 'https://cartieai.com';

const buildBadgeUrl = (label, value, color, host = PROD) => {
  const params = new URLSearchParams({ label, value, color });
  return `${host}/api/badge.svg?${params.toString()}`;
};

const gradeColors = {
  'A+': 'from-emerald-500/30 to-emerald-500/5 border-emerald-500/40 text-emerald-300',
  'A':  'from-emerald-500/25 to-emerald-500/5 border-emerald-500/40 text-emerald-300',
  'B':  'from-yellow-500/25 to-yellow-500/5 border-yellow-500/40 text-yellow-300',
  'C':  'from-amber-500/25 to-amber-500/5 border-amber-500/40 text-amber-300',
  'D':  'from-orange-500/25 to-orange-500/5 border-orange-500/40 text-orange-300',
  'F':  'from-rose-500/25 to-rose-500/5 border-rose-500/40 text-rose-300',
};

const BillScore = () => {
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);     // 0..N-1 = question; N = result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDone, setEmailDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eEmail, setEEmail] = useState('');
  const [eName, setEName] = useState('');
  const [eCompany, setECompany] = useState('');

  // Load quiz definition once
  useEffect(() => {
    axios.get(`${API_URL}/bill-score/questions`).then(({ data }) => {
      setQuestions(data.questions || []);
    }).catch(() => {
      toast.error('Could not load the quiz — please refresh.');
    });
  }, []);

  const totalSteps = questions.length;
  const isResultStep = step >= totalSteps && totalSteps > 0;
  const currentQ = questions[step];
  const progressPct = totalSteps ? Math.round((step / totalSteps) * 100) : 0;

  const choose = async (qKey, value) => {
    const next = { ...answers, [qKey]: value };
    setAnswers(next);
    if (step + 1 < totalSteps) {
      setStep(step + 1);
    } else {
      // Final question answered → compute
      setLoading(true);
      try {
        const { data } = await axios.post(`${API_URL}/bill-score/score`, { answers: next });
        setResult(data);
        setStep(totalSteps);
      } catch (e) {
        toast.error("Couldn't compute your score", { description: e?.message });
      } finally { setLoading(false); }
    }
  };

  const restart = () => {
    setStep(0); setAnswers({}); setResult(null);
  };

  const previous = () => {
    if (step > 0 && !isResultStep) setStep(step - 1);
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!eEmail.trim()) { toast.error('A work email helps us reach you'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/bill-score/email-plan`, {
        email: eEmail.trim(),
        name: eName.trim() || undefined,
        company: eCompany.trim() || undefined,
        answers,
        score: result.score,
      });
      setEmailDone(true);
      toast.success('Welcome — your tailored plan is on its way');
    } catch (err) {
      toast.error("Couldn't save just now", { description: err?.response?.data?.detail || err?.message });
    } finally { setSubmitting(false); }
  };

  const shareCopy = () => {
    if (!result) return;
    const text = `${result.share.headline}\n\nTake the quiz → ${PROD}/bill-score`;
    navigator.clipboard.writeText(text);
    toast.success('Copied — paste anywhere');
  };

  const shareTwitter = () => {
    if (!result) return;
    const text = encodeURIComponent(`${result.share.headline} ${PROD}/bill-score`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
  };

  const shareLinkedin = () => {
    if (!result) return;
    const url = encodeURIComponent(`${PROD}/bill-score`);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="bill-score-page">
      <nav className="fixed top-0 w-full z-50 bg-slate-900 text-slate-100 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <Badge className="mb-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 tracking-wider">
              <Trophy className="w-3 h-3 mr-1" /> 60-SECOND QUIZ · NO SIGN-UP
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-3">
              What's your <span className="text-emerald-600">Bill Score</span>?
            </h1>
            <p className="text-slate-600 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
              Five questions. A 0–100 score on how well you handle your cloud bill — with a personalised action plan, transparent scoring, and a sharable badge so your team can compare.
            </p>
          </div>

          {/* Progress bar (only during quiz) */}
          {!isResultStep && totalSteps > 0 && (
            <div className="mb-6" data-testid="quiz-progress">
              <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                <span>Question {Math.min(step + 1, totalSteps)} of {totalSteps}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Quiz card */}
          {!isResultStep && currentQ && (
            <Card className="bg-slate-900 text-slate-100 border-emerald-500/20" data-testid="quiz-card">
              <CardHeader>
                <CardTitle className="text-white text-lg">{currentQ.label}</CardTitle>
                <CardDescription className="text-slate-400">Pick whichever is closest to true. There's no wrong answer — only honest ones.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {currentQ.options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={loading}
                    onClick={() => choose(currentQ.key, opt.value)}
                    data-testid={`option-${currentQ.key}-${opt.value}`}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      answers[currentQ.key] === opt.value
                        ? 'border-emerald-500 bg-emerald-500/10 text-white'
                        : 'border-white/10 hover:border-emerald-500/40 hover:bg-white/5 text-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{opt.label}</span>
                      <ArrowRight className="h-4 w-4 opacity-30 group-hover:opacity-100" />
                    </div>
                  </button>
                ))}
                <div className="flex justify-between pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={previous}
                    disabled={step === 0 || loading}
                    data-testid="prev-btn"
                    className="text-slate-400 hover:text-white"
                  >
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" />Back
                  </Button>
                  {loading && <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Result */}
          {isResultStep && result && (
            <div className="space-y-6" data-testid="quiz-result">
              {/* Hero score */}
              <Card className={`bg-gradient-to-br ${gradeColors[result.grade.letter] || gradeColors.B} border-2`}>
                <CardContent className="p-8 text-center">
                  <p className="text-xs uppercase tracking-wider opacity-70 mb-2">Your Bill Score</p>
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <p className="text-7xl sm:text-8xl font-black font-mono text-slate-900 leading-none" data-testid="score-value">{result.score}</p>
                    <div className="text-left">
                      <p className="text-5xl font-extrabold text-slate-900">{result.grade.letter}</p>
                      <p className="text-sm font-semibold opacity-80">{result.grade.label}</p>
                    </div>
                  </div>
                  <p className="text-sm opacity-90 max-w-xl mx-auto">{result.grade.blurb}</p>
                  <div className="flex gap-2 mt-6 justify-center flex-wrap">
                    <Button onClick={shareTwitter} variant="outline" className="border-white/20 text-slate-900 hover:bg-white/10" data-testid="share-twitter-btn">
                      <Twitter className="h-4 w-4 mr-2" />Share on X
                    </Button>
                    <Button onClick={shareLinkedin} variant="outline" className="border-white/20 text-slate-900 hover:bg-white/10" data-testid="share-linkedin-btn">
                      <Linkedin className="h-4 w-4 mr-2" />LinkedIn
                    </Button>
                    <Button onClick={shareCopy} variant="outline" className="border-white/20 text-slate-900 hover:bg-white/10" data-testid="share-copy-btn">
                      <Copy className="h-4 w-4 mr-2" />Copy
                    </Button>
                    <Button onClick={restart} variant="ghost" className="text-slate-900 hover:bg-white/10" data-testid="restart-btn">
                      <RefreshCcw className="h-4 w-4 mr-2" />Retake
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Sharable badge */}
              <Card className="bg-slate-900 text-slate-100 border-white/10" data-testid="badge-card">
                <CardHeader>
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-emerald-400" />Wear it on your README
                  </CardTitle>
                  <CardDescription className="text-slate-400">Drop this badge in your repo's README — it links back to the quiz so your team can compete.</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center gap-4">
                  <img
                    src={buildBadgeUrl('Bill Score', result.share.value, result.share.color, process.env.REACT_APP_BACKEND_URL)}
                    alt={`Bill Score: ${result.share.value}`}
                    height={20}
                    data-testid="result-badge"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      const md = `[![Bill Score: ${result.share.value}](${buildBadgeUrl('Bill Score', result.share.value, result.share.color)})](${PROD}/bill-score)`;
                      navigator.clipboard.writeText(md);
                      toast.success('Markdown copied');
                    }}
                    variant="outline"
                    className="border-emerald-500/30 text-emerald-300"
                    data-testid="copy-badge-md-btn"
                  >
                    <Copy className="h-3 w-3 mr-1.5" />Copy Markdown
                  </Button>
                </CardContent>
              </Card>

              {/* Recommendations */}
              {result.recommendations.length > 0 && (
                <Card className="bg-slate-900 text-slate-100 border-white/10" data-testid="recommendations-card">
                  <CardHeader>
                    <CardTitle className="text-base text-white flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-emerald-400" />Top {result.recommendations.length} actions for you
                    </CardTitle>
                    <CardDescription className="text-slate-400">Picked from your weakest answers — biggest impact first.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {result.recommendations.map((r, i) => (
                      <div key={r.key} className="p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`rec-${r.key}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <p className="font-semibold text-white">{i + 1}. {r.title}</p>
                          <Link to={r.url}>
                            <Button size="sm" variant="ghost" className="text-emerald-300 hover:text-emerald-200 shrink-0">
                              See in product <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          </Link>
                        </div>
                        <p className="text-sm text-slate-400 mb-2">{r.why}</p>
                        <p className="text-xs text-slate-400"><strong className="text-slate-400">Do today:</strong> {r.do_now}</p>
                        <p className="text-xs text-emerald-300 mt-1"><strong>Where CARTIEAI helps:</strong> {r.cartie_helps}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Score breakdown */}
              <Card className="bg-slate-900 text-slate-100 border-white/10" data-testid="breakdown-card">
                <CardHeader>
                  <CardTitle className="text-base text-white">How the {result.score} adds up</CardTitle>
                  <CardDescription className="text-slate-400">Equal weighting · 20 points each · public scoring rubric.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {result.breakdown.map((b) => (
                    <div key={b.key} className="flex items-center justify-between p-2 rounded-md bg-white/5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{b.label}</p>
                        {!b.skipped && b.answer_label && (
                          <p className="text-xs text-slate-400 truncate">→ {b.answer_label}</p>
                        )}
                      </div>
                      <span className={`shrink-0 ml-3 font-mono font-bold ${b.score >= b.max * 0.7 ? 'text-emerald-400' : b.score >= b.max * 0.4 ? 'text-amber-400' : 'text-rose-400'}`}>
                        {b.score} / {b.max}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Email plan CTA */}
              <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30">
                <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">Want this as a tailored plan in your inbox?</p>
                    <p className="text-sm text-slate-400 mt-1">Our founder will email <strong className="text-white">your</strong> 3-action plan within one business day. No spam, no marketing list.</p>
                  </div>
                  <Button
                    onClick={() => { setEmailDone(false); setEmailOpen(true); }}
                    className="bg-emerald-500 hover:bg-emerald-400 text-white"
                    data-testid="email-plan-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />Email me the plan
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-dialog">
          {emailDone ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-500">
                  <CheckCircle2 className="h-5 w-5" />Welcome — you're in.
                </DialogTitle>
                <DialogDescription>
                  We'll email <strong>{eEmail}</strong> within one business day with a tailored plan based on your answers and your score of <strong>{result?.score}/100</strong>.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => setEmailOpen(false)} data-testid="email-close-btn">Sounds good</Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submitEmail}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-emerald-500" />Email me the tailored plan
                </DialogTitle>
                <DialogDescription>
                  No drip campaign. Just one personal reply within a business day.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-600">Work email *</label>
                  <Input type="email" required value={eEmail} onChange={(e) => setEEmail(e.target.value)} placeholder="you@yourcompany.com" autoFocus data-testid="email-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-600">Your name</label>
                    <Input value={eName} onChange={(e) => setEName(e.target.value)} placeholder="Jane Doe" data-testid="name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-600">Company</label>
                    <Input value={eCompany} onChange={(e) => setECompany(e.target.value)} placeholder="Acme Corp" data-testid="company-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEmailOpen(false)} data-testid="email-cancel-btn">Maybe later</Button>
                <Button type="submit" disabled={submitting} className="bg-emerald-500 hover:bg-emerald-400 text-white" data-testid="email-submit-btn">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : <><Mail className="h-4 w-4 mr-2" />Send me the plan</>}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <section className="px-4 pb-10">
        <div className="max-w-3xl mx-auto">
          <FAQSection
            title="Common questions"
            items={faqsByPage.bill_score}
            testIdPrefix="bill-score-faq"
            variant="card"
          />
        </div>
      </section>

      <footer className="py-6 bg-slate-950 border-t border-white/5 text-center text-xs text-slate-400">
        © 2026 CARTIEAI · <Link to="/privacy-policy" className="hover:text-slate-600">Privacy</Link> · <Link to="/terms" className="hover:text-slate-600">Terms</Link> · <Link to="/faq" className="hover:text-slate-600">FAQ</Link>
      </footer>
    </div>
  );
};

export default BillScore;
